import { InboxEntry, InboxEntryPriority } from '@this+that/models';

import {
  DueDateFilter,
  PriorityFilter,
} from '../../../../components/DoBoxFilters/types';
import type { DoBoxFilterSortState } from '../../../../store/DoBox/useDoBoxFilterSortStore';

export type DoBoxViewFilters = Pick<
  DoBoxFilterSortState,
  | 'selectedCardStatusFilter'
  | 'selectedFilterLabelIds'
  | 'showNoLabelsFilter'
  | 'selectedFilterMemberIds'
  | 'showNoMembersFilter'
  | 'selectedDueDateFilters'
  | 'selectedPriorityFilters'
>;

export type AttributeFilterableEntry = Pick<
  InboxEntry,
  'taskLabelIds' | 'participantIds' | 'priority' | 'dueAt'
>;

type AttributeFilters = Omit<DoBoxViewFilters, 'selectedCardStatusFilter'>;

const PRIORITY_FILTER_TO_ENTRY: Record<string, InboxEntryPriority> = {
  [PriorityFilter.URGENT]: InboxEntryPriority.URGENT,
  [PriorityFilter.HIGH]: InboxEntryPriority.HIGH,
  [PriorityFilter.MEDIUM]: InboxEntryPriority.MEDIUM,
  [PriorityFilter.LOW]: InboxEntryPriority.LOW,
};

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const DAYS_IN_WEEK = 7;
const DAYS_IN_MONTH = 30;

export function matchesDueDateFilter(
  dueAt: number | null | undefined,
  filter: DueDateFilter,
): boolean {
  const now = Date.now();
  switch (filter) {
    case DueDateFilter.NO_DATES:
      return !dueAt;
    case DueDateFilter.OVERDUE:
      return !!dueAt && dueAt < now;
    case DueDateFilter.DUE_NEXT_DAY:
      return !!dueAt && dueAt >= now && dueAt <= now + ONE_DAY_MS;
    case DueDateFilter.DUE_NEXT_WEEK:
      return (
        !!dueAt && dueAt >= now && dueAt <= now + DAYS_IN_WEEK * ONE_DAY_MS
      );
    case DueDateFilter.DUE_NEXT_MONTH:
      return (
        !!dueAt && dueAt >= now && dueAt <= now + DAYS_IN_MONTH * ONE_DAY_MS
      );
    default:
      return false;
  }
}

const matchesLabelFilter = (
  entry: AttributeFilterableEntry,
  { selectedFilterLabelIds, showNoLabelsFilter }: AttributeFilters,
): boolean => {
  if (selectedFilterLabelIds.length > 0) {
    return selectedFilterLabelIds.every((id) =>
      entry.taskLabelIds?.includes(id),
    );
  }
  if (showNoLabelsFilter) {
    return (entry.taskLabelIds?.length ?? 0) === 0;
  }
  return true;
};

const matchesMemberFilter = (
  entry: AttributeFilterableEntry,
  { selectedFilterMemberIds, showNoMembersFilter }: AttributeFilters,
): boolean => {
  if (!showNoMembersFilter && selectedFilterMemberIds.length === 0) {
    return true;
  }
  if (showNoMembersFilter && (entry.participantIds?.length ?? 0) === 0) {
    return true;
  }
  return selectedFilterMemberIds.some((id) =>
    entry.participantIds?.includes(id),
  );
};

const matchesPriorityFilter = (
  entry: AttributeFilterableEntry,
  { selectedPriorityFilters }: AttributeFilters,
): boolean =>
  selectedPriorityFilters.length === 0 ||
  selectedPriorityFilters.some((filter) =>
    filter === PriorityFilter.NO_PRIORITY
      ? entry.priority == null
      : entry.priority === PRIORITY_FILTER_TO_ENTRY[filter],
  );

const matchesDueDateFilters = (
  entry: AttributeFilterableEntry,
  { selectedDueDateFilters }: AttributeFilters,
): boolean =>
  selectedDueDateFilters.length === 0 ||
  selectedDueDateFilters.some((filter) =>
    matchesDueDateFilter(entry.dueAt, filter),
  );

export const matchesDoBoxAttributeFilters = (
  entry: AttributeFilterableEntry,
  filters: AttributeFilters,
): boolean =>
  matchesLabelFilter(entry, filters) &&
  matchesMemberFilter(entry, filters) &&
  matchesPriorityFilter(entry, filters) &&
  matchesDueDateFilters(entry, filters);
